import styled from "styled-components";
import { SpringConfig, animated, useSpring } from "@react-spring/web";
import { H4, Icon, IconButton, useResponsive } from "@introist/react-foundation/v2";

import { AppMenuEntry } from "./AppMenuEntry";
import { MenuCollapsedContext } from "./context";
import { AppMenuItem, WorkspaceSelectorProps } from "./types";
import { WorkspaceSelector } from "./WorkspaceSelector";

const AnimatedAppMenu = styled(animated.div)`
  position: fixed;
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: var(--palette-surface-subdued);
  border-right: 1px solid var(--palette-border-subdued);
  z-index: 1;
  overflow: hidden;
`;

// Always scrollable, with the gutter reserved, so the items don't shift when the scrollbar
// appears. The scrollbar itself only shows while hovering: scrollbar-color in Chrome and Firefox,
// the ::-webkit-scrollbar rules in Safari (Chrome ignores those once scrollbar-color is set).
const Content = styled.div`
  padding-top: var(--spacing-large);
  box-sizing: border-box;

  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  ::-webkit-scrollbar {
    width: 6px;
  }
  ::-webkit-scrollbar-thumb {
    background-color: transparent;
    border-radius: 3px;
  }

  :hover {
    scrollbar-color: var(--palette-border-default) transparent;

    ::-webkit-scrollbar-thumb {
      background-color: var(--palette-border-default);
    }
  }
`;

const AnimatedGhostMenu = styled(animated.div)`
  height: 100vh;
  position: relative;
  flex-shrink: 0;
`;

const Wrapper = styled.div`
  padding: 0 1rem;
`;

const AnimatedCollapseButton = styled(animated(IconButton))`
  width: 1.5rem;
  height: 1.5rem;
  margin-top: var(--spacing-medium);
  margin-left: auto;
  background-color: transparent;
  border-color: var(--palette-border-subdued);

  &:hover {
    && {
      background-color: transparent;
      border-color: var(--palette-primary-default);
    }
  }

  > div {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MainNavigation = styled.nav`
  margin-top: var(--spacing-xxLarge);
  display: grid;
  grid-row-gap: var(--spacing-xSmall);
  overflow: hidden;
`;

const BottomNavigation = styled(MainNavigation)`
  margin-top: auto;
  padding-bottom: var(--spacing-large);
`;

const MENU_WIDTH_PX = { collapsed: 76, open: 216 };
const SPRING_CONFIG: SpringConfig = { tension: 240, clamp: true };

// Width of the product rail, when shown. The side menu sits right of it.
export const PRODUCT_MENU_WIDTH_PX = 51;

export type AppMenuShellProps = {
  collapsed?: boolean;
  // Shown as the menu's heading when the product rail is visible; the workspace selector is in
  // the rail then. Without a product, the selector is at the top of this menu.
  product?: string;
  items: AppMenuItem[];
  bottomItems?: AppMenuItem[];
  workspace: WorkspaceSelectorProps;
  onToggleCollapse: () => void;
};

export const AppMenuShell = ({
  collapsed = false,
  product,
  items,
  bottomItems,
  workspace,
  onToggleCollapse
}: AppMenuShellProps) => {
  const { isMobileLayout } = useResponsive();

  const spring = useSpring({
    width: collapsed ? MENU_WIDTH_PX.collapsed : MENU_WIDTH_PX.open,
    config: SPRING_CONFIG
  });

  const mobileSpring = useSpring({
    transform: collapsed ? "translateX(-100%)" : "translateX(0%)",
    width: "100vw"
  });

  const marginLeft = product ? `${PRODUCT_MENU_WIDTH_PX}px` : undefined;

  return (
    <MenuCollapsedContext.Provider value={collapsed}>
      {
        // Takes the menu's width in the page flow, pushing the app content right.
        !isMobileLayout && <AnimatedGhostMenu style={{ ...spring, marginLeft }} />
      }
      <AnimatedAppMenu style={{ ...(isMobileLayout ? mobileSpring : spring), marginLeft }}>
        <Content>
          <Wrapper>
            {product === undefined && <WorkspaceSelector variant="full" {...workspace} />}

            {product && <H4 style={{ whiteSpace: "nowrap" }}>{collapsed ? " " : product}</H4>}
            <AnimatedCollapseButton
              style={{ marginRight: collapsed ? "auto" : undefined }}
              icon={
                <Icon
                  name="collapseDrawer"
                  style={{ transform: collapsed ? "rotateZ(180deg)" : undefined }}
                />
              }
              variant="outlined"
              onClick={onToggleCollapse}
            />
          </Wrapper>
          <MainNavigation>
            {items.map((item, index) => (
              <AppMenuEntry key={`app-menu-item-${index}`} {...item} />
            ))}
          </MainNavigation>

          {bottomItems && (
            <BottomNavigation>
              {bottomItems.map((item, index) => (
                <AppMenuEntry key={`app-menu-bottom-item-${index}`} {...item} />
              ))}
            </BottomNavigation>
          )}
        </Content>
      </AnimatedAppMenu>
    </MenuCollapsedContext.Provider>
  );
};
